import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { ScrollCollapseProvider } from '~/components/ScrollCollapse/contexts/ScrollCollapseProvider';
import { SlideOut } from '~/components/SlideOut/SlideOut';
import { DataUnavailable } from '~/modules/artist-roster/components/DataUnavailable/DataUnavailable';
import { ArtistsTable } from '~/modules/artist-roster/components/Roster/ArtistsTable';
import { RosterHeader } from '~/modules/artist-roster/components/Roster/RosterHeader';
import { useArtistColumns } from '~/modules/artist-roster/hooks/useArtistColumns';
import { useArtistQuery } from '~/modules/artist-roster/hooks/useArtistQuery';
import { useRosterUrlParams } from '~/modules/artist-roster/hooks/useRosterUrlParams';
import { getLabels } from '~/modules/artist-roster/lib/labels';
import { getRosterStatus } from '~/modules/artist-roster/lib/roster';
import { type LabelData } from '~/modules/artist-roster/lib/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { ArtistFilterPanel } from '../ArtistFilter/ArtistFilterPanel';
import type { RosterFilters } from '../ArtistFilter/types';

export function RosterPage() {
  const { t } = useTranslation('roster');
  const { trackEvent } = useAnalytics('Artist Roster');
  const { columns } = useArtistColumns();
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const rosterUrlParamsProps = useRosterUrlParams();
  const { filters, pageNumber, setFilters } = rosterUrlParamsProps;
  const { labelIds, searchText, isCrossover, isOnRoster } = filters;
  // These are the full LabelData objects filtered by the current labelIds.
  const [filteredLabels, setFilteredLabels] = useState<LabelData[]>([]);

  // Current filter values passed to the filter panel. Note this is different
  // to the filters returned from useRosterUrlParams because these have the
  // full LabelData objects instead of just the labelIds
  const currentFilterValues: RosterFilters = useMemo(() => {
    return {
      labels: filteredLabels.map((label) => ({
        labelId: label.labelId,
        name: label.name,
      })),
      isCrossover,
      isOnRoster,
    };
  }, [filteredLabels, isCrossover, isOnRoster]);

  const artistTableMetadata = useMemo(() => {
    return {
      filtersCount: labelIds.length,
      filtersApplied: labelIds.length > 0,
      crossoverFilterApplied: isCrossover,
      rosterStatus: getRosterStatus(isOnRoster),
    };
  }, [isCrossover, labelIds.length, isOnRoster]);

  const {
    data: artistsData,
    error,
    isFetching: isFetchingArtists,
  } = useArtistQuery({
    searchText,
    labelIds,
    isCrossover,
    isOnRoster,
    pageNumber,
  });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pageNumber]);

  const { data: labelData } = useQuery({
    queryKey: ['labels'],
    queryFn: () => getLabels({}),
  });

  // Update the filtered label data once the data loads.
  useEffect(() => {
    if (labelIds && labelData) {
      const filteredData = labelData.filter((label: LabelData) =>
        labelIds.includes(label.labelId),
      );

      setFilteredLabels(filteredData);
    }
  }, [labelData, labelIds]);

  const handleApplyFilters = useCallback(
    (appliedFilters: RosterFilters) => {
      setFilteredLabels(appliedFilters.labels);
      setFilters({
        isCrossover: appliedFilters.isCrossover,
        isOnRoster: appliedFilters.isOnRoster,
        labelIds: appliedFilters.labels.map((label) => label.labelId),
        searchText,
      });

      trackEvent('rosterLabelFiltersApplied', {
        roster_label_filters_count: appliedFilters.labels.length,
        roster_crossover_filter_applied: appliedFilters.isCrossover,
        roster_label_filters_applied: appliedFilters.labels.length > 0,
        roster_status: getRosterStatus(appliedFilters.isOnRoster),
      });
    },
    [searchText, setFilters, trackEvent],
  );

  useEffect(() => {
    trackEvent('rosterView');
  }, [trackEvent]);

  if (error) {
    return (
      <DataUnavailable
        title={t('errorData.title')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('header.heading')}</title>
      </Helmet>
      <ScrollCollapseProvider scrollRef={scrollRef}>
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto overscroll-contain [overflow-anchor:none]"
        >
          <RosterHeader
            {...{
              rosterUrlParamsProps,
              filteredLabels,
              setFilteredLabels,
              setIsFilterPanelOpen,
            }}
          />
          <ArtistsTable
            columns={columns}
            meta={artistTableMetadata}
            data={artistsData?.items ?? []}
            isLoading={isFetchingArtists}
          />
        </div>
      </ScrollCollapseProvider>
      <SlideOut
        {...{
          open: isFilterPanelOpen,
          onOpenChange: setIsFilterPanelOpen,
        }}
      >
        <ArtistFilterPanel
          originalFilters={currentFilterValues}
          onApplyFilters={handleApplyFilters}
        />
      </SlideOut>
    </>
  );
}

RosterPage.displayName = 'RosterPage';
